import { observable, action, computed, configure, runInAction } from "mobx";
import { createContext, SyntheticEvent } from "react";
import { toast } from "react-toastify";
import agent from "../api/agent";
import { ITimeSheet } from "../models/timesheet";

configure({ enforceActions: "always" });

class TimeSheetStore {
  @observable timesheetallRegistry = new Map();
  @observable timesheetpayrunRegistry = new Map();
  @observable timesheetemployeeRegistry = new Map();
  @observable payrunRegistry = new Map();
  @observable payrollcycleRegistry = new Map();
  @observable employeeRegistry = new Map();
  @observable employeepayrunRegistry = new Map();
  @observable employeestatusRegistry = new Map();
  @observable payrunweekRegistry = new Map();
  @observable projecttimeRegistry = new Map();
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";

  remarkRegistry = new Map();

  @computed get timesheetsByDate() {
    return Array.from(this.timesheetemployeeRegistry.values()).sort(
      (a, b) => Date.parse(a.attendanceDate) - Date.parse(b.attendanceDate)
    );
  }

  @computed get payrunstatusByDate() {
    return Array.from(this.payrunRegistry.values()).sort(
      (a, b) => a.financeYear - b.financeYear || a.payRun.localeCompare(b.payRun)
    );
  }

  @computed get payrunsheetByDate() {
    return Array.from(this.timesheetpayrunRegistry.values()).sort(
      (a, b) => a.payRun.localeCompare(b.payRun)
    );
  }

  @computed get payrollcyclesByName() {
    return Array.from(this.payrollcycleRegistry.values()).sort(
      (a, b) =>
        a.endYear.localeCompare(b.endYear) ||
        a.payRun.localeCompare(b.payRun)
    );
  }

  @computed get employeeByName() {
    return Array.from(this.employeepayrunRegistry.values()).sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }

  @computed get employeeAllByName() {
    return Array.from(this.employeeRegistry.values()).sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }

  @action loadPayrollCycles = async () => {
    this.loadingInitial = true;
    try {
      const payrollcycles = await agent.PayrollCycles.list();
      runInAction("loading payroll cycles", () => {
        payrollcycles.forEach((payrollcycle) => {
          if (Date.parse(payrollcycle.startDate) < Date.now())
          this.payrollcycleRegistry.set(payrollcycle.endYear + "-" + payrollcycle.payRun, payrollcycle);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load payroll cycles error", () => {
        this.loadingInitial = false;
      });
      toast.error("load payroll cycles error");
    }
  };

  @action loadReviewTimeSheets = async () => {
    let initialPayrun = '';
    try {
      const timesheets = await agent.TimeSheets.list('','');
      runInAction("loading time sheets", () => {
        this.timesheetallRegistry.clear();
        this.payrunRegistry.clear();
        timesheets.forEach((timesheet) => {
          if (timesheet.status === 'Submitted' || timesheet.status === 'Reviewed' || 
          timesheet.status === 'Rejected')
          {
            timesheet.attendanceDate = timesheet.attendanceDate.substr(0, 10);
            this.timesheetallRegistry.set(timesheet.id, timesheet);
            this.payrunRegistry.set(timesheet.financeYear+timesheet.payRun, timesheet);
          }
        });
        this.loadingInitial = false;
       
      });
      const sortPayrun = Array.from(this.payrunRegistry.values()).sort(
        (b, a) => a.financeYear - b.financeYear || a.payRun.localeCompare(b.payRun));
      if (sortPayrun.length > 0) initialPayrun = sortPayrun[0].financeYear + '-' + sortPayrun[0].payRun;
      this.selectPayRun(initialPayrun);
    } catch (error) {
      runInAction("load time sheets Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load time sheets Error");
    }
    return initialPayrun;
  };

  @action loadApproveTimeSheets = async () => {
    let initialPayrun = '';
    try {
      const timesheets = await agent.TimeSheets.list('','');
      runInAction("loading time sheets", () => {
        this.timesheetallRegistry.clear();
        this.payrunRegistry.clear();
        timesheets.forEach((timesheet) => {
          if (timesheet.status === 'Reviewed' || timesheet.status === 'Approved')
          {
            timesheet.attendanceDate = timesheet.attendanceDate.substr(0, 10);
            this.timesheetallRegistry.set(timesheet.id, timesheet);
            this.payrunRegistry.set(timesheet.financeYear+timesheet.payRun, timesheet);
          }
        });
        
        this.loadingInitial = false;
      });
      const sortPayrun = Array.from(this.payrunRegistry.values()).sort(
        (b, a) => a.financeYear - b.financeYear || a.payRun.localeCompare(b.payRun));
      if (sortPayrun.length > 0) initialPayrun = sortPayrun[0].financeYear + '-' + sortPayrun[0].payRun;
      this.selectPayRun(initialPayrun);
      return initialPayrun;
    } catch (error) {
      runInAction("load time sheets Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load time sheets Error");
    }
    return initialPayrun;
  };

  @action editTimeSheets = async (payrun: string, email: string, status: string) => {
    this.submitting = true;
    this.target = status+email;
    let remarks = Array.from(this.remarkRegistry.values());
    try {
      await agent.TimeSheets.update(payrun, email, status, remarks);
      runInAction("editing employee time sheet", () => {
        this.timesheetemployeeRegistry.forEach((timesheet) => {
            timesheet.status = status;
        });
        this.employeestatusRegistry.set(email, status);
        this.submitting = false;
        toast.success('Time Sheet '+status);
      });
    } catch (error) {
      runInAction("edit Time Sheet error", () => {
        this.submitting = false;
      });
      toast.error("edit Time Sheet error");
      console.log(error);
    }
  };

  @action selectPayRun = async (payRunFinance: string) => {
    const year = payRunFinance.split('-')[0];
    const payRun = payRunFinance.split('-')[1];
    let timeSheets = Array.from(this.timesheetallRegistry.values());
    const selectedTimeSheets = timeSheets.filter((timeSheet) =>
        timeSheet.payRun === payRun && timeSheet.financeYear === year
    );
    this.employeepayrunRegistry.clear();
    this.employeestatusRegistry.clear();
    this.timesheetpayrunRegistry.clear();
    this.timesheetemployeeRegistry.clear();
    this.payrunweekRegistry.clear();
    this.remarkRegistry.clear();
    selectedTimeSheets.forEach((timeSheet) => {
      if (this.employeeRegistry.get(timeSheet.employeeEmail))
      {
        const emp = this.employeeRegistry.get(timeSheet.employeeEmail);
        this.employeepayrunRegistry.set(timeSheet.employeeEmail, emp);
        this.employeestatusRegistry.set(timeSheet.employeeEmail, timeSheet.status);
        this.timesheetpayrunRegistry.set(timeSheet.id, timeSheet);
      }
    });
  };

  @action selectEmployee = async (email: string) => {
    let timeSheets = Array.from(this.timesheetpayrunRegistry.values());
    const selectedTimeSheets = timeSheets.filter((timeSheet) =>
        timeSheet.employeeEmail === email
    ).sort(
      (a, b) => Date.parse(a.attendanceDate) - Date.parse(b.attendanceDate)
    );
    this.timesheetemployeeRegistry.clear();
    this.payrunweekRegistry.clear();
    selectedTimeSheets.forEach((timeSheet, index) => {
        if (index < 7) this.payrunweekRegistry.set(timeSheet.attendanceDate, 'week1')
        else this.payrunweekRegistry.set(timeSheet.attendanceDate, 'week2');
        if (timeSheet.attendant === 'Y') this.timesheetemployeeRegistry.set(timeSheet.id, timeSheet);
    });
  };

  @action updateRemark = async (id: string, value: string) => {
    let timesheet = this.timesheetemployeeRegistry.get(id);
    timesheet.remark = value;
    this.timesheetemployeeRegistry.set(id, timesheet);
    this.remarkRegistry.set(id, {id: id, remark: value});
  };

  @action updateRemarkApprove = async (id: string, value: string) => {
    let timesheet = this.timesheetpayrunRegistry.get(id);
    timesheet.remark = value;
    this.timesheetpayrunRegistry.set(id, timesheet);
    this.remarkRegistry.set(id, {id: id, remark: value});
  };

  @action loadEmployees = async () => {
    try {
      const employees = await agent.Employees.list();
      runInAction("loading empolyees", () => {
        this.employeeRegistry.clear();
        employees.forEach((employee) => {
          this.employeeRegistry.set(employee.email, employee);
        });
      });
    } catch (error) {
      runInAction("load Employee List Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load Employee List Error");
    }
  };

  @action loadProjectTimes = async () => {
    // this.loadingInitial = true;
    try {
      const projecttimes = await agent.ProjectTimes.list();
      runInAction('loading projecttimes', () => {
        projecttimes.forEach(projecttime => {
          this.projecttimeRegistry.set(projecttime.code, projecttime);
        });
      })
    } catch (error) {
      runInAction('load Project Time List error' ,() => {
        this.loadingInitial = false;
      })
      toast.error('load Project Time List error');
    }
  };

  @action loadEmployeePayrunTimeSheets = async (employee: string, payrun: string) => {
    let initialPayrun : ITimeSheet[] = [];
    try {
      const timesheets = await agent.TimeSheets.list(employee,payrun);
      runInAction("loading time sheets", () => {
        timesheets.forEach((timesheet) => {
          timesheet.attendanceDate = timesheet.attendanceDate.substr(0, 10);
          initialPayrun.push(timesheet);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load time sheets Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load time sheets Error");
    }
    return initialPayrun;
  };

  @action editEmployeeTimeSheets = async (employeeTimeSheets: ITimeSheet[], status: string, email: string) => {
    this.submitting = true;
    for (let i = 0; i < employeeTimeSheets.length; i ++)
    {
      employeeTimeSheets[i].status = status;
      employeeTimeSheets[i].reviewerEmail = email;
    };
    try {
      await agent.EmployeeTimeSheets.update(employeeTimeSheets);
      runInAction("editing employee time sheet", () => {
        this.submitting = false;
        toast.success('Time sheet '+ status.toLowerCase() + ' successfully!');
      });
    } catch (error) {
      runInAction("edit employee error", () => {
        this.submitting = false;
      });
      toast.error("edit employee error");
      console.log(error);
    }
  };

  @action clearPage = () => {
    this.timesheetallRegistry.clear();
    this.timesheetpayrunRegistry.clear();
    this.timesheetemployeeRegistry.clear();
    this.payrunRegistry.clear();
    this.payrollcycleRegistry.clear();
    this.employeeRegistry.clear();
    this.employeepayrunRegistry.clear();
    this.employeestatusRegistry.clear();
    this.payrunweekRegistry.clear();
    this.remarkRegistry.clear();
  }

}

export default createContext(new TimeSheetStore());
